import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import VolverOnboardingBtn from '../../components/VolverOnboardingBtn';
import {
  Users, Search, Plus, Edit3, X, Save, ChevronLeft, ChevronRight,
  AlertCircle, AlertTriangle, Upload, Download, FileSpreadsheet, Loader2, ChevronDown, Eye,
  Mail, Phone, Calendar, Globe, Briefcase, Building2, MapPin, Award, Trash2, KeyRound,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const PER_PAGE = 10;

// Lista de paises (ISO + ES). Mantener simple en frontend. "Otro" siempre al final.
const PAISES = [
  ...[
    'Chile', 'Argentina', 'Bolivia', 'Brasil', 'Colombia', 'Costa Rica', 'Cuba', 'Ecuador',
    'El Salvador', 'España', 'Estados Unidos', 'Francia', 'Guatemala', 'Haití', 'Honduras',
    'Italia', 'Japón', 'México', 'Nicaragua', 'Panamá', 'Paraguay', 'Perú', 'Portugal',
    'Puerto Rico', 'República Dominicana', 'Uruguay', 'Venezuela', 'Alemania', 'Reino Unido',
    'Canadá', 'Australia', 'China', 'Corea del Sur', 'India', 'Rusia', 'Sudáfrica',
  ].sort((a, b) => a.localeCompare(b, 'es')),
  'Otro',
];

const toTitleCase = (s) => s ? s.toLowerCase().replace(/(?:^|\s|[-/])\S/g, c => c.toUpperCase()) : '';

// Formatea RUT al escribir: agrega puntos cada 3 dígitos y guión antes del DV
const formatRutOnChange = (raw) => {
  const clean = raw.replace(/[^0-9kK]/g, '').toUpperCase();
  if (clean.length <= 1) return clean;
  const body = clean.slice(0, -1);
  const dv = clean.slice(-1);
  const formattedBody = body.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${formattedBody}-${dv}`;
};

const formatRut = (rut) => {
  if (!rut) return '';
  const clean = rut.replace(/[.\-]/g, '');
  if (clean.length < 2) return clean;
  const body = clean.slice(0, -1);
  const dv = clean.slice(-1);
  return `${body.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}-${dv.toUpperCase()}`;
};

const formatPhone = (phone) => {
  if (!phone) return '-';
  const clean = phone.replace(/[^0-9]/g, '');
  if (!clean) return '-';
  if (clean.length === 9 && clean.startsWith('9')) return `+56${clean}`;
  if (clean.length === 11 && clean.startsWith('569')) return `+${clean}`;
  if (clean.length === 12 && clean.startsWith('569')) return `+${clean}`;
  return phone;
};

const validarRut = (rut) => {
  const clean = rut.replace(/[.\-]/g, '').trim().toUpperCase();
  if (clean.length < 2) return false;
  const body = clean.slice(0, -1);
  const dv = clean.slice(-1);
  if (!/^\d+$/.test(body)) return false;
  let total = 0, factor = 2;
  for (let i = body.length - 1; i >= 0; i--) {
    total += parseInt(body[i]) * factor;
    factor = factor < 7 ? factor + 1 : 2;
  }
  const remainder = 11 - (total % 11);
  const expected = remainder === 11 ? '0' : remainder === 10 ? 'K' : String(remainder);
  return dv === expected;
};

const emptyForm = {
  rut: '', nombre: '', apellido: '',
  email: '', fono: '', movil: '', cargo: '',
  sexo: '', fecha_nacimiento: '', nacionalidad: '',
  gerencia_id: '', superintendencia_id: '',
  contrato_lb_id: '',
  areas: [], actividades: [],
};

const DetailRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2.5 py-1.5">
    <Icon className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
    <div className="flex-1 min-w-0">
      <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">{label}</p>
      <p className="text-sm text-slate-800 break-words">{value}</p>
    </div>
  </div>
);

export default function EseTrabajadores() {
  const { token, user } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [trabajadores, setTrabajadores] = useState([]);
  const [actividadesCatalog, setActividadesCatalog] = useState([]);
  const [areasCatalog, setAreasCatalog] = useState([]);
  const [gerenciasCatalog, setGerenciasCatalog] = useState([]);
  const [superCatalog, setSuperCatalog] = useState([]);
  const [contratosCatalog, setContratosCatalog] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [rutError, setRutError] = useState('');
  const [showBulk, setShowBulk] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [actSearch, setActSearch] = useState('');
  const [paisSearch, setPaisSearch] = useState('');
  const [showPaisDropdown, setShowPaisDropdown] = useState(false);
  const [paisOtro, setPaisOtro] = useState(false);  // true cuando el usuario eligió "Otro"
  const [viewing, setViewing] = useState(null);  // trabajador for detail modal
  const [deleting, setDeleting] = useState(null);  // trabajador for delete confirmation
  const [deletingBusy, setDeletingBusy] = useState(false);
  const [resetting, setResetting] = useState(null); // trabajador for reset password confirmation
  const [resettingBusy, setResettingBusy] = useState(false);
  const [resetResult, setResetResult] = useState(null); // { email, default_password } tras reset
  // Reasignación
  const [reasignModal, setReasignModal] = useState(null); // { trabajadorId, empresaOrigen, ... }
  const [reasignForm, setReasignForm] = useState({ motivo: '', file: null });
  const [reasignBusy, setReasignBusy] = useState(false);

  const fetchTrabajadores = useCallback(async () => {
    try {
      const [r1, r2, r3, r4, r5, r6] = await Promise.all([
        axios.get(`${API}/ese/trabajadores`, { headers }),
        axios.get(`${API}/ese/actividades`, { headers }),
        axios.get(`${API}/ese/areas`, { headers }),
        axios.get(`${API}/ese/gerencias`, { headers }),
        axios.get(`${API}/ese/superintendencias`, { headers }),
        axios.get(`${API}/ese/contratos`, { headers }).catch(() => ({ data: [] })),
      ]);
      setTrabajadores(r1.data || []);
      setActividadesCatalog(r2.data || []);
      setAreasCatalog(r3.data || []);
      setGerenciasCatalog(r4.data || []);
      setSuperCatalog(r5.data || []);
      setContratosCatalog(r6.data || []);
    } catch { toast.error('Error al cargar trabajadores'); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchTrabajadores(); }, [fetchTrabajadores]);

  const filtered = trabajadores.filter(t => {
    const q = search.toLowerCase();
    if (!q) return true;
    const cleanQ = q.replace(/[.\-]/g, '');
    const cleanRut = (t.rut || '').replace(/[.\-]/g, '').toLowerCase();
    return t.nombre_completo?.toLowerCase().includes(q) || cleanRut.includes(cleanQ) || (t.email || '').toLowerCase().includes(q) || (t.cargo || '').toLowerCase().includes(q);
  });

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  useEffect(() => { setPage(1); }, [search]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setRutError(''); setActSearch(''); setPaisSearch(''); setPaisOtro(false); setShowModal(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      rut: formatRut(t.rut || ''), nombre: t.nombre || '', apellido: t.apellido || '',
      email: t.email || '', fono: t.fono || '', movil: t.movil || '', cargo: t.cargo || '',
      sexo: t.sexo || '', fecha_nacimiento: t.fecha_nacimiento || '', nacionalidad: t.nacionalidad || '',
      gerencia_id: t.gerencia_id || '', superintendencia_id: t.superintendencia_id || '',
      contrato_lb_id: t.contrato_lb_id ? String(t.contrato_lb_id) : '',
      areas: (t.areas || []).map(a => a.id),
      actividades: (t.actividades || []).map(a => a.id),
    });
    // Si la nacionalidad guardada no está en el catálogo, asumimos "Otro" con texto personalizado
    const nac = t.nacionalidad || '';
    const isInList = nac && PAISES.includes(nac) && nac !== 'Otro';
    const esOtro = !!nac && !isInList;
    setPaisOtro(esOtro);
    setRutError(''); setActSearch(''); setPaisSearch(esOtro ? 'Otro' : nac); setShowModal(true);
  };

  const rutCheckTimer = React.useRef(null);
  const handleRutChange = (val) => {
    const formatted = formatRutOnChange(val);
    setForm({ ...form, rut: formatted });
    const clean = formatted.replace(/[.\-]/g, '').trim();
    if (clean.length >= 2) {
      setRutError(validarRut(formatted) ? '' : 'Digito verificador invalido');
    } else {
      setRutError('');
    }
    // Debounced RUT ownership check (solo en creación, no en edición y solo si RUT es válido)
    if (rutCheckTimer.current) clearTimeout(rutCheckTimer.current);
    if (editing) return;
    if (!validarRut(formatted)) return;
    rutCheckTimer.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/ese/trabajadores/check-rut`, { headers, params: { rut: formatted } });
        const s = res.data;
        if (s.status === 'belongs_to_other') {
          // Bloquear guardar y abrir modal de reasignación de inmediato.
          setRutError(`Este RUT pertenece a "${s.empresa_origen_nombre}". Solicite reasignación.`);
          setShowModal(false);
          setReasignModal({
            trabajador_id: s.trabajador_id,
            rut: formatted,
            nombre_snapshot: `${s.nombre || ''} ${s.apellido || ''}`.trim() || '(sin nombre)',
            empresa_origen_nombre: s.empresa_origen_nombre,
            message: `Este RUT pertenece a "${s.empresa_origen_nombre}". Solicite reasignación adjuntando la carta de justificación.`,
          });
          setReasignForm({ motivo: '', file: null });
        } else if (s.status === 'already_in_my_empresa') {
          setRutError('Este trabajador ya está registrado en su empresa.');
        }
      } catch { /* silencioso: no bloquear la escritura */ }
    }, 500);
  };

  const toggleActividad = (id) => {
    setForm(f => ({ ...f, actividades: f.actividades.includes(id) ? f.actividades.filter(x => x !== id) : [...f.actividades, id] }));
  };
  const toggleArea = (id) => {
    setForm(f => ({ ...f, areas: f.areas.includes(id) ? f.areas.filter(x => x !== id) : [...f.areas, id] }));
  };

  // Filtra superintendencias por gerencia_id seleccionada
  const filteredSupers = form.gerencia_id ? superCatalog.filter(s => s.gerencia_id === form.gerencia_id) : [];
  // Filtra países preservando "Otro" siempre al final
  const filteredPaises = (() => {
    const base = PAISES.filter(p => p !== 'Otro');
    const matched = paisSearch ? base.filter(p => p.toLowerCase().includes(paisSearch.toLowerCase())) : base;
    return [...matched, 'Otro'];
  })();

  const handleSave = async () => {
    if (!form.rut || !form.nombre) { toast.error('RUT y nombre son obligatorios'); return; }
    if (!validarRut(form.rut)) { toast.error('RUT invalido: el digito verificador no coincide'); return; }
    const email = (form.email || '').trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error('El correo electrónico es obligatorio y debe ser válido'); return; }
    setSaving(true);
    const payload = {
      ...form,
      contrato_lb_id: form.contrato_lb_id ? parseInt(form.contrato_lb_id, 10) : null,
    };
    try {
      if (editing) {
        await axios.put(`${API}/ese/trabajadores/${editing.id}`, payload, { headers });
        toast.success('Trabajador actualizado');
      } else {
        try {
          const res = await axios.post(`${API}/ese/trabajadores`, payload, { headers });
          const dp = res.data?.default_password;
          const reVinculado = res.data?.re_vinculado;
          if (reVinculado) {
            toast.success(res.data?.message || 'Trabajador re-vinculado', { duration: 7000 });
          } else if (dp && form.email) {
            toast.success(`Trabajador creado. Acceso al Perfil: ${form.email} / ${dp}`, { duration: 8000 });
          } else {
            toast.success('Trabajador creado');
          }
        } catch (err) {
          // 409 = trabajador pertenece a otra empresa → abrir modal Solicitar Reasignación
          if (err.response?.status === 409 && err.response?.data?.detail?.code === 'worker_belongs_to_other_company') {
            const d = err.response.data.detail;
            setShowModal(false);
            setReasignModal({
              trabajador_id: d.trabajador_id,
              rut: form.rut,
              nombre_snapshot: `${form.nombre} ${form.apellido || ''}`.trim(),
              empresa_origen_nombre: d.empresa_origen_nombre,
              message: d.message,
            });
            setReasignForm({ motivo: '', file: null });
            setSaving(false);
            return;
          }
          throw err;
        }
      }
      setShowModal(false);
      fetchTrabajadores();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Error al guardar';
      toast.error(detail);
      // Si el error indica que el trabajador ya existe en la empresa, refrescamos la lista
      // porque probablemente fue creado en un intento previo y la UI está desincronizada.
      const yaExiste = typeof detail === 'string' && (
        detail.toLowerCase().includes('ya esta registrado') ||
        detail.toLowerCase().includes('ya está registrado')
      );
      if (yaExiste) {
        setShowModal(false);
        fetchTrabajadores();
      }
    }
    setSaving(false);
  };

  const downloadPlantilla = async () => {
    try {
      const res = await axios.get(`${API}/ese/trabajadores/plantilla`, {
        headers, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'plantilla_trabajadores.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Plantilla descargada');
    } catch { toast.error('No se pudo descargar la plantilla'); }
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) { toast.error('Selecciona un archivo Excel'); return; }
    setBulkLoading(true); setBulkResult(null);
    try {
      const fd = new FormData();
      fd.append('file', bulkFile);
      const res = await axios.post(`${API}/ese/trabajadores/carga-masiva`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      setBulkResult(res.data);
      const { created, linked, errors } = res.data;
      toast.success(`${created} creados, ${linked} vinculados${errors?.length ? `, ${errors.length} errores` : ''}`);
      fetchTrabajadores();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error en la carga masiva');
    }
    setBulkLoading(false);
  };

  const filteredActs = actividadesCatalog.filter(a => !actSearch || a.nombre.toLowerCase().includes(actSearch.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="ese-trabajadores">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <VolverOnboardingBtn user={user} />
          <h1 className="text-2xl font-bold text-slate-900">Trabajadores</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { setShowBulk(true); setBulkFile(null); setBulkResult(null); }}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg text-sm font-medium hover:bg-emerald-100 transition-colors"
            data-testid="bulk-upload-btn">
            <Upload className="w-4 h-4" />Carga Masiva
          </button>
          <button onClick={openCreate}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
            data-testid="crear-trabajador-btn">
            <Plus className="w-4 h-4" />Nuevo Trabajador
          </button>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por nombre, RUT, email o cargo..."
            className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm bg-white" data-testid="search-trabajador" />
        </div>
        <span className="text-sm text-slate-400 shrink-0">{filtered.length} trabajadores</span>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left px-4 py-3 text-slate-500 font-medium">#</th>
              <th className="text-left px-4 py-3 text-slate-500 font-medium">RUT</th>
              <th className="text-left px-4 py-3 text-slate-500 font-medium">Nombre</th>
              <th className="text-left px-4 py-3 text-slate-500 font-medium">Cargo</th>
              <th className="text-left px-4 py-3 text-slate-500 font-medium">Contrato</th>
              <th className="text-left px-4 py-3 text-slate-500 font-medium">Gerencia Lomas Bayas</th>
              <th className="text-center px-4 py-3 text-slate-500 font-medium">Areas</th>
              <th className="text-center px-4 py-3 text-slate-500 font-medium">Activs.</th>
              <th className="text-right px-4 py-3 text-slate-500 font-medium">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((t, i) => (
              <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                <td className="px-4 py-3 text-slate-400">{(page - 1) * PER_PAGE + i + 1}</td>
                <td className="px-4 py-3 font-mono text-xs">{formatRut(t.rut)}</td>
                <td className="px-4 py-3 font-medium text-slate-800">{toTitleCase(t.nombre_completo)}</td>
                <td className="px-4 py-3 text-slate-600 text-xs">{toTitleCase(t.cargo) || <span className="text-slate-300">—</span>}</td>
                <td className="px-4 py-3 text-slate-600 text-xs font-mono">
                  {t.contrato_numero_acuerdo
                    ? <span title={t.contrato_descripcion || ''}>{t.contrato_numero_acuerdo}</span>
                    : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-4 py-3 text-slate-600 text-xs">{t.contrato_gerencia_nombre || t.gerencia_nombre || <span className="text-slate-300">—</span>}</td>
                <td className="px-4 py-3 text-center">
                  <span className="inline-flex items-center justify-center min-w-[24px] px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-50 text-blue-700">
                    {(t.areas || []).length}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <span className="inline-flex items-center justify-center min-w-[24px] px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-50 text-amber-700">
                    {(t.actividades || []).length}
                  </span>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => setViewing(t)}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-slate-600 bg-slate-100 rounded-lg text-xs hover:bg-slate-200 transition-colors mr-1.5"
                    data-testid={`view-trabajador-${t.id}`}>
                    <Eye className="w-3 h-3" />Ver
                  </button>
                  <button onClick={() => openEdit(t)}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-blue-600 bg-blue-50 rounded-lg text-xs hover:bg-blue-100 transition-colors mr-1.5"
                    data-testid={`edit-trabajador-${t.id}`}>
                    <Edit3 className="w-3 h-3" />Editar
                  </button>
                  <button onClick={() => setResetting(t)}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-amber-700 bg-amber-50 rounded-lg text-xs hover:bg-amber-100 transition-colors mr-1.5"
                    title="Restablecer clave al default (primeros 5 dígitos del RUT)"
                    data-testid={`reset-trabajador-${t.id}`}>
                    <KeyRound className="w-3 h-3" />Clave
                  </button>
                  <button onClick={() => setDeleting(t)}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-red-600 bg-red-50 rounded-lg text-xs hover:bg-red-100 transition-colors"
                    data-testid={`delete-trabajador-${t.id}`}>
                    <Trash2 className="w-3 h-3" />Eliminar
                  </button>
                </td>
              </tr>
            ))}
            {paginated.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-400">
                <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
                No se encontraron trabajadores
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-white rounded-xl border border-slate-200 px-4 py-3">
          <span className="text-sm text-slate-500">{(page - 1) * PER_PAGE + 1}-{Math.min(page * PER_PAGE, filtered.length)} de {filtered.length}</span>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
              className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let p;
              if (totalPages <= 5) p = i + 1;
              else if (page <= 3) p = i + 1;
              else if (page >= totalPages - 2) p = totalPages - 4 + i;
              else p = page - 2 + i;
              return (
                <button key={p} onClick={() => setPage(p)}
                  className={`w-8 h-8 rounded-lg text-sm font-medium ${p === page ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>{p}</button>
              );
            })}
            <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages}
              className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {/* Modal Individual */}
      {showModal && createPortal(
        <div style={{ backgroundColor: "rgba(0,0,0,0.5)" }} className="fixed inset-0 z-[60] flex items-center justify-center p-4 overflow-y-auto" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl my-8" onClick={ev => ev.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900">{editing ? 'Editar Trabajador' : 'Nuevo Trabajador'}</h2>
              <button onClick={() => setShowModal(false)} className="p-1.5 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
              {/* Datos Personales */}
              <section>
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Datos Personales</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">RUT *</label>
                    <input value={form.rut} onChange={e => handleRutChange(e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg text-sm ${rutError ? 'border-red-300 bg-red-50/50' : 'border-slate-200'}`}
                      placeholder="12.345.678-9" data-testid="input-rut" />
                    {rutError && (
                      <p className="flex items-center gap-1 mt-1 text-xs text-red-500">
                        <AlertCircle className="w-3 h-3" />{rutError}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Sexo</label>
                    <select value={form.sexo} onChange={e => setForm({ ...form, sexo: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white" data-testid="input-sexo">
                      <option value="">Sin especificar</option>
                      <option value="Masculino">Masculino</option>
                      <option value="Femenino">Femenino</option>
                      <option value="Otro">Otro</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Nombre *</label>
                    <input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="input-nombre" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Apellido</label>
                    <input value={form.apellido} onChange={e => setForm({ ...form, apellido: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="input-apellido" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Fecha de Nacimiento</label>
                    <input type="date" value={form.fecha_nacimiento || ''} onChange={e => setForm({ ...form, fecha_nacimiento: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="input-fecha-nac" />
                  </div>
                  <div className="relative">
                    <label className="block text-xs font-medium text-slate-600 mb-1">Pais de Origen</label>
                    <input
                      value={paisSearch}
                      onChange={e => {
                        const val = e.target.value;
                        setPaisSearch(val);
                        setShowPaisDropdown(true);
                        if (paisOtro) setPaisOtro(false);
                        setForm({ ...form, nacionalidad: val });
                      }}
                      onFocus={() => setShowPaisDropdown(true)}
                      onBlur={() => setTimeout(() => setShowPaisDropdown(false), 200)}
                      placeholder="Buscar pais..."
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                      data-testid="input-pais" />
                    {showPaisDropdown && filteredPaises.length > 0 && (
                      <div className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg">
                        {filteredPaises.slice(0, 40).map(p => (
                          <button
                            key={p}
                            type="button"
                            onMouseDown={() => {
                              if (p === 'Otro') {
                                setPaisOtro(true);
                                setForm({ ...form, nacionalidad: '' });
                                setPaisSearch('Otro');
                              } else {
                                setPaisOtro(false);
                                setForm({ ...form, nacionalidad: p });
                                setPaisSearch(p);
                              }
                              setShowPaisDropdown(false);
                            }}
                            className={`w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 ${p === 'Otro' ? 'text-blue-700 font-medium border-t border-slate-100' : 'text-slate-700'}`}
                            data-testid={`pais-${p}`}>
                            {p}
                          </button>
                        ))}
                      </div>
                    )}
                    {paisOtro && (
                      <input
                        value={form.nacionalidad}
                        onChange={e => setForm({ ...form, nacionalidad: e.target.value })}
                        placeholder="Escribe el pais..."
                        className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm mt-2 bg-blue-50/30"
                        data-testid="input-pais-otro" />
                    )}
                  </div>
                </div>
              </section>

              {/* Contacto */}
              <section>
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Contacto</h3>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Email *</label>
                  <input type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                    placeholder="nombre@empresa.cl"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="input-email" />
                  <p className="text-[10px] text-slate-500 mt-1">Requerido: se usa para notificar cierre de nómina y acceso del trabajador.</p>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Movil</label>
                    <input value={form.movil} onChange={e => setForm({ ...form, movil: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="+569XXXXXXXX" data-testid="input-movil" />
                  </div>
                  <div></div>
                </div>
              </section>

              {/* Datos Laborales */}
              <section>
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Datos Laborales</h3>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Cargo</label>
                  <input value={form.cargo} onChange={e => setForm({ ...form, cargo: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="Operador" data-testid="input-cargo" />
                </div>
                {/* Contrato asignado (filtrado por empresa del ESE) */}
                <div className="mt-3">
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Contrato asignado <span className="text-slate-400 font-normal">(define la gerencia)</span>
                  </label>
                  {contratosCatalog.length === 0 ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-xs text-amber-900" data-testid="alert-sin-contratos">
                      <p className="font-semibold flex items-center gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5" />
                        Su empresa no tiene contratos registrados
                      </p>
                      <p className="mt-1 text-amber-800">
                        Solicite la creación de su contrato en el <strong>Módulo Carpeta de Arranque</strong> para proceder con la asignación.
                      </p>
                    </div>
                  ) : (
                    <>
                      <select value={form.contrato_lb_id || ''}
                        onChange={e => {
                          const cid = e.target.value;
                          const c = contratosCatalog.find(x => String(x.id) === cid);
                          if (!c) {
                            setForm(prev => ({ ...prev, contrato_lb_id: cid }));
                            return;
                          }
                          // Auto-match: gerencia + superintendencia por nombre (normalizado)
                          const norm = (s) => (s || '').toString().toLocaleLowerCase('es').trim().replace(/\s+/g, ' ');
                          const gTarget = norm(c.nombre_gerencia);
                          const sTarget = norm(c.nombre_subgerencia);
                          const gMatch = gTarget ? gerenciasCatalog.find(g => norm(g.nombre) === gTarget) : null;
                          const sMatch = (gMatch && sTarget)
                            ? superCatalog.find(s => s.gerencia_id === gMatch.id && norm(s.nombre) === sTarget)
                            : null;
                          setForm(prev => ({
                            ...prev,
                            contrato_lb_id: cid,
                            gerencia_id: gMatch ? gMatch.id : prev.gerencia_id,
                            superintendencia_id: sMatch ? sMatch.id : (gMatch ? '' : prev.superintendencia_id),
                          }));
                        }}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                        data-testid="select-contrato-lb">
                        <option value="">-- Seleccionar contrato --</option>
                        {contratosCatalog.map(c => (
                          <option key={c.id} value={String(c.id)}>
                            {c.numero_acuerdo} — {(c.descripcion || 'Sin descripción').slice(0, 60)}
                          </option>
                        ))}
                      </select>
                      {form.contrato_lb_id && (() => {
                        const c = contratosCatalog.find(x => String(x.id) === String(form.contrato_lb_id));
                        if (!c) return null;
                        return (
                          <div className="mt-2 bg-teal-50 border border-teal-100 rounded-lg px-3 py-2 text-xs text-teal-800 space-y-0.5">
                            <p><strong>Gerencia LB:</strong> {c.nombre_gerencia || '—'}</p>
                            {c.nombre_subgerencia && <p><strong>Superintendencia:</strong> {c.nombre_subgerencia}</p>}
                          </div>
                        );
                      })()}
                    </>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Gerencia Lomas Bayas</label>
                    <select value={form.gerencia_id}
                      onChange={e => setForm({ ...form, gerencia_id: e.target.value, superintendencia_id: '' })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                      data-testid="select-gerencia">
                      <option value="">-- Seleccionar --</option>
                      {gerenciasCatalog.map(g => <option key={g.id} value={g.id}>{g.nombre}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1 whitespace-nowrap">
                      Superintendencia Lomas Bayas
                      {!form.gerencia_id && <span className="text-slate-400 font-normal"> (selecciona gerencia primero)</span>}
                    </label>
                    <select value={form.superintendencia_id}
                      onChange={e => setForm({ ...form, superintendencia_id: e.target.value })}
                      disabled={!form.gerencia_id}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white disabled:bg-slate-50 disabled:text-slate-400"
                      data-testid="select-superintendencia">
                      <option value="">{form.gerencia_id ? '-- Seleccionar --' : '--'}</option>
                      {filteredSupers.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                    </select>
                  </div>
                </div>
                {/* Áreas multi-selector */}
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-xs font-medium text-slate-600">
                      Areas ({form.areas.length})
                      <span className="ml-2 text-[10px] text-slate-400 font-normal">Habilitan capacitaciones por area</span>
                    </label>
                    <div className="flex items-center gap-2 text-[10px]">
                      <button type="button" onClick={() => setForm({ ...form, areas: areasCatalog.map(a => a.id) })} className="text-blue-600 hover:underline">Todas</button>
                      <button type="button" onClick={() => setForm({ ...form, areas: [] })} className="text-slate-500 hover:underline">Ninguna</button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-40 overflow-y-auto border border-slate-100 rounded-lg p-2 bg-slate-50/30">
                    {areasCatalog.map(a => {
                      const checked = form.areas.includes(a.id);
                      return (
                        <label key={a.id} className={`flex items-center gap-2 rounded-md border px-2 py-1 cursor-pointer text-xs transition-colors ${checked ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
                          data-testid={`area-${a.id}`}>
                          <input type="checkbox" checked={checked} onChange={() => toggleArea(a.id)} className="w-3.5 h-3.5 accent-blue-600" />
                          <span className="text-slate-800 flex-1 truncate">{a.nombre}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </section>

              {/* Actividades */}
              <section>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Actividades ({form.actividades.length}/{actividadesCatalog.length})</h3>
                  <div className="flex items-center gap-2 text-[10px]">
                    <button onClick={() => setForm({ ...form, actividades: filteredActs.map(a => a.id) })} className="text-emerald-600 hover:underline">Todas</button>
                    <button onClick={() => setForm({ ...form, actividades: [] })} className="text-slate-500 hover:underline">Ninguna</button>
                  </div>
                </div>
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-2.5 w-3.5 h-3.5 text-slate-400" />
                  <input value={actSearch} onChange={e => setActSearch(e.target.value)} placeholder="Buscar actividad..."
                    className="w-full pl-8 pr-3 py-1.5 border border-slate-200 rounded-lg text-xs" data-testid="actividad-search" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-56 overflow-y-auto pr-1 border border-slate-100 rounded-lg p-2 bg-slate-50/30">
                  {filteredActs.map(a => {
                    const checked = form.actividades.includes(a.id);
                    return (
                      <label key={a.id} className={`flex items-center gap-2 rounded-md border px-2 py-1.5 cursor-pointer text-xs transition-colors ${checked ? 'border-amber-500 bg-amber-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
                        data-testid={`act-${a.id}`}>
                        <input type="checkbox" checked={checked} onChange={() => toggleActividad(a.id)} className="w-3.5 h-3.5 accent-amber-600" />
                        <span className="text-slate-800 flex-1 truncate">{a.nombre}</span>
                      </label>
                    );
                  })}
                  {filteredActs.length === 0 && <p className="text-xs text-slate-400 italic py-3 text-center col-span-2">Sin resultados.</p>}
                </div>
              </section>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancelar</button>
              <button onClick={handleSave} disabled={saving || !!rutError}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                data-testid="save-trabajador-btn">
                <Save className="w-4 h-4" />{saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Modal Detalle Trabajador */}
      {viewing && createPortal(
        <div style={{ backgroundColor: "rgba(0,0,0,0.5)" }} className="fixed inset-0 z-[60] flex items-center justify-center p-4 overflow-y-auto" onClick={() => setViewing(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl my-8" onClick={ev => ev.stopPropagation()} data-testid="view-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-blue-600 to-blue-700 rounded-t-xl">
              <div className="text-white">
                <h2 className="text-lg font-bold">{toTitleCase(viewing.nombre_completo)}</h2>
                <p className="text-xs text-white/80 font-mono">{formatRut(viewing.rut)}</p>
              </div>
              <button onClick={() => setViewing(null)} className="p-1.5 hover:bg-white/10 rounded-lg text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Datos Personales */}
                <section className="rounded-lg border border-slate-200 p-4">
                  <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Datos Personales</h3>
                  <DetailRow icon={Calendar} label="Fecha Nacimiento" value={viewing.fecha_nacimiento ? new Date(viewing.fecha_nacimiento + 'T12:00:00').toLocaleDateString('es-CL') : '—'} />
                  <DetailRow icon={Globe} label="Pais de Origen" value={viewing.nacionalidad || '—'} />
                  <DetailRow icon={Users} label="Sexo" value={viewing.sexo || '—'} />
                </section>

                {/* Contacto */}
                <section className="rounded-lg border border-slate-200 p-4">
                  <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Contacto</h3>
                  <DetailRow icon={Mail} label="Email" value={viewing.email ? <a href={`mailto:${viewing.email}`} className="text-blue-600 hover:underline">{viewing.email}</a> : '—'} />
                  <DetailRow icon={Phone} label="Movil" value={viewing.movil ? formatPhone(viewing.movil) : (viewing.fono ? formatPhone(viewing.fono) : '—')} />
                </section>
              </div>

              {/* Datos Laborales */}
              <section className="rounded-lg border border-slate-200 p-4">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Datos Laborales</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                  <DetailRow icon={Briefcase} label="Cargo" value={toTitleCase(viewing.cargo) || '—'} />
                  <DetailRow icon={Briefcase} label="Contrato Asignado" value={viewing.contrato_numero_acuerdo ? `${viewing.contrato_numero_acuerdo}${viewing.contrato_descripcion ? ` — ${viewing.contrato_descripcion}` : ''}` : '—'} />
                  <DetailRow icon={Building2} label="Gerencia Lomas Bayas" value={viewing.contrato_gerencia_nombre || viewing.gerencia_nombre || '—'} />
                  <DetailRow icon={Building2} label="Superintendencia Lomas Bayas" value={viewing.superintendencia_nombre || '—'} />
                </div>
              </section>

              {/* Areas */}
              <section className="rounded-lg border border-slate-200 p-4">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5 text-blue-500" /> Areas Asignadas ({(viewing.areas || []).length})
                </h3>
                {(viewing.areas || []).length === 0 ? (
                  <p className="text-xs text-slate-400 italic">Sin areas asignadas.</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {viewing.areas.map(a => (
                      <span key={a.id} className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-md border border-blue-200">
                        {a.nombre}
                      </span>
                    ))}
                  </div>
                )}
              </section>

              {/* Actividades */}
              <section className="rounded-lg border border-slate-200 p-4">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Award className="w-3.5 h-3.5 text-amber-500" /> Actividades Asignadas ({(viewing.actividades || []).length})
                </h3>
                {(viewing.actividades || []).length === 0 ? (
                  <p className="text-xs text-slate-400 italic">Sin actividades asignadas.</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {viewing.actividades.map(a => (
                      <span key={a.id} className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-50 text-amber-700 text-xs font-medium rounded-md border border-amber-200">
                        {a.nombre}
                      </span>
                    ))}
                  </div>
                )}
              </section>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-3 border-t border-slate-200 bg-slate-50 rounded-b-xl">
              <button onClick={() => setViewing(null)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-200 rounded-lg">Cerrar</button>
              <button onClick={() => { const t = viewing; setViewing(null); openEdit(t); }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                data-testid="view-edit-btn">
                <Edit3 className="w-4 h-4" /> Editar
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Modal Carga Masiva */}
      {showBulk && createPortal(
        <div style={{ backgroundColor: "rgba(0,0,0,0.5)" }} className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={() => !bulkLoading && setShowBulk(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-xl" onClick={ev => ev.stopPropagation()} data-testid="bulk-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-emerald-600" /> Carga Masiva de Trabajadores
              </h2>
              <button onClick={() => !bulkLoading && setShowBulk(false)} className="p-1.5 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4">
                <p className="text-xs text-emerald-900 font-medium mb-2">Pasos para la carga masiva:</p>
                <ol className="text-xs text-emerald-800 list-decimal pl-4 space-y-0.5">
                  <li>Descarga la plantilla Excel</li>
                  <li>Completa los datos de tus trabajadores (no modifiques los encabezados)</li>
                  <li>Sube el archivo aqui mismo</li>
                </ol>
                <button onClick={downloadPlantilla}
                  className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-emerald-300 text-emerald-700 rounded-md text-xs font-medium hover:bg-emerald-50 transition-colors"
                  data-testid="download-template-btn">
                  <Download className="w-3.5 h-3.5" /> Descargar plantilla.xlsx
                </button>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-2">Archivo Excel (.xlsx)</label>
                <input type="file" accept=".xlsx,.xls"
                  onChange={e => setBulkFile(e.target.files[0])}
                  className="block w-full text-xs text-slate-700 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  data-testid="bulk-file-input" />
                {bulkFile && <p className="mt-2 text-xs text-slate-500">Archivo seleccionado: <span className="font-medium">{bulkFile.name}</span></p>}
              </div>
              {bulkResult && (
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs">
                  <p className="font-semibold text-slate-700 mb-1">Resultado:</p>
                  <ul className="space-y-0.5 text-slate-600">
                    <li>✅ <span className="font-medium">{bulkResult.created}</span> trabajadores creados</li>
                    <li>🔗 <span className="font-medium">{bulkResult.linked}</span> trabajadores existentes vinculados</li>
                    {bulkResult.errors?.length > 0 && (
                      <li className="text-red-600">
                        ⚠️ <span className="font-medium">{bulkResult.errors.length}</span> filas con error:
                        <ul className="ml-4 mt-1 list-disc text-red-500 max-h-32 overflow-y-auto">
                          {bulkResult.errors.slice(0, 20).map((e, i) => <li key={i}>{e}</li>)}
                        </ul>
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200">
              <button onClick={() => !bulkLoading && setShowBulk(false)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">
                {bulkResult ? 'Cerrar' : 'Cancelar'}
              </button>
              <button onClick={handleBulkUpload} disabled={!bulkFile || bulkLoading}
                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                data-testid="bulk-process-btn">
                {bulkLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Procesando...</> : <><Upload className="w-4 h-4" /> Subir y procesar</>}
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Modal Confirmar Eliminación */}
      {deleting && createPortal(
        <div style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          onClick={() => !deletingBusy && setDeleting(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="modal-delete-trabajador">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-slate-900">Eliminar trabajador</h3>
                <p className="text-sm text-slate-500 mt-1">
                  ¿Confirma que desea eliminar a{' '}
                  <span className="font-semibold text-slate-800">{toTitleCase(deleting.nombre_completo)}</span>
                  {' '}({formatRut(deleting.rut)}) de su empresa?
                </p>
                <p className="text-xs text-slate-400 mt-2">
                  El trabajador se desvincula de su empresa. Su historial de capacitaciones e inscripciones se conserva.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" disabled={deletingBusy} onClick={() => setDeleting(null)}
                className="px-4 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                data-testid="delete-cancel-btn">
                Cancelar
              </button>
              <button type="button" disabled={deletingBusy}
                onClick={async () => {
                  setDeletingBusy(true);
                  try {
                    await axios.delete(`${API}/ese/trabajadores/${deleting.id}`, { headers });
                    toast.success('Trabajador eliminado.');
                    setDeleting(null);
                    fetchTrabajadores();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Error al eliminar');
                  }
                  setDeletingBusy(false);
                }}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold disabled:opacity-50"
                data-testid="delete-confirm-btn">
                {deletingBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> Eliminando...</> : <><Trash2 className="w-4 h-4" /> Eliminar</>}
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Modal Restablecer Contraseña */}
      {resetting && !resetResult && createPortal(
        <div style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          onClick={() => !resettingBusy && setResetting(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="modal-reset-password">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center shrink-0">
                <KeyRound className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-slate-900">Restablecer contraseña</h3>
                <p className="text-sm text-slate-500 mt-1">
                  ¿Confirma restablecer la clave de{' '}
                  <span className="font-semibold text-slate-800">{toTitleCase(resetting.nombre_completo)}</span>
                  {' '}({formatRut(resetting.rut)})?
                </p>
                <p className="text-xs text-slate-400 mt-2">
                  La clave volverá a ser los <strong>primeros 5 dígitos del RUT</strong>. El trabajador podrá cambiarla luego en su perfil.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" disabled={resettingBusy} onClick={() => setResetting(null)}
                className="px-4 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                data-testid="reset-cancel-btn">
                Cancelar
              </button>
              <button type="button" disabled={resettingBusy}
                onClick={async () => {
                  setResettingBusy(true);
                  try {
                    const res = await axios.post(`${API}/ese/trabajadores/${resetting.id}/reset-password`, {}, { headers });
                    setResetResult(res.data);
                    toast.success('Contraseña restablecida.');
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Error al restablecer la contraseña');
                    setResetting(null);
                  }
                  setResettingBusy(false);
                }}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-semibold disabled:opacity-50"
                data-testid="reset-confirm-btn">
                {resettingBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> Restableciendo...</> : <><KeyRound className="w-4 h-4" /> Restablecer</>}
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Modal Resultado Reset — muestra la clave por defecto para que el ESE la comparta */}
      {resetResult && createPortal(
        <div style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          onClick={() => { setResetResult(null); setResetting(null); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="modal-reset-result">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center shrink-0">
                <KeyRound className="w-5 h-5 text-emerald-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-slate-900">Clave restablecida</h3>
                <p className="text-sm text-slate-500 mt-1">Comparta estos datos con el trabajador. Podrá cambiarla desde su perfil.</p>
              </div>
            </div>
            <div className="space-y-2 text-sm bg-slate-50 border border-slate-200 rounded-lg p-3">
              <div className="flex justify-between gap-2">
                <span className="text-slate-500">Email</span>
                <span className="font-medium text-slate-800 break-all">{resetResult.email}</span>
              </div>
              <div className="flex justify-between gap-2 items-center">
                <span className="text-slate-500">Clave por defecto</span>
                <span className="font-mono text-base font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5" data-testid="reset-default-password">
                  {resetResult.default_password}
                </span>
              </div>
            </div>
            <div className="flex justify-end mt-5">
              <button type="button" onClick={() => { setResetResult(null); setResetting(null); }}
                className="px-4 py-2 text-sm bg-slate-800 hover:bg-slate-900 text-white rounded-lg font-semibold"
                data-testid="reset-close-btn">
                Cerrar
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Modal Solicitar Reasignación */}
      {reasignModal && createPortal(
        <div style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          onClick={() => !reasignBusy && setReasignModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6" onClick={e => e.stopPropagation()} data-testid="modal-reasignar">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-lg font-bold text-slate-900">Solicitar Reasignación</h3>
                <p className="text-xs text-slate-500 mt-1">
                  RUT <span className="font-mono">{reasignModal.rut}</span> pertenece a{' '}
                  <span className="font-semibold text-slate-800">{reasignModal.empresa_origen_nombre}</span>.
                </p>
              </div>
              <button onClick={() => !reasignBusy && setReasignModal(null)} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 mb-4 text-xs text-amber-900">
              Al aprobar la reasignación por el Capacitador, este trabajador deberá rehacer la inducción/conducción bajo su empresa.
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-600">Carta de Reasignación (PDF) *</label>
                <input type="file" accept="application/pdf"
                  onChange={e => setReasignForm(f => ({ ...f, file: e.target.files?.[0] || null }))}
                  className="mt-1 w-full text-xs" data-testid="reasign-file-input" />
                {reasignForm.file && (
                  <p className="text-[10px] text-slate-500 mt-1">{reasignForm.file.name} · {(reasignForm.file.size / 1024).toFixed(0)} KB</p>
                )}
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Motivo (opcional)</label>
                <textarea value={reasignForm.motivo} onChange={e => setReasignForm(f => ({ ...f, motivo: e.target.value }))}
                  rows={3} placeholder="Ej. Trabajador se cambió a nuestra empresa el 01-09-2026."
                  className="mt-1 w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button type="button" disabled={reasignBusy} onClick={() => { setReasignModal(null); setForm(emptyForm); setRutError(''); }}
                className="px-4 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50">
                Cancelar
              </button>
              <button type="button" disabled={reasignBusy || !reasignForm.file}
                onClick={async () => {
                  if (!reasignForm.file) return;
                  if (reasignForm.file.type !== 'application/pdf') { toast.error('La carta debe ser PDF'); return; }
                  if (reasignForm.file.size > 5 * 1024 * 1024) { toast.error('El archivo excede 5 MB'); return; }
                  setReasignBusy(true);
                  try {
                    // 1. Sube la carta al bucket "reasignaciones"
                    const fd = new FormData(); fd.append('file', reasignForm.file);
                    const up = await axios.post(
                      `${API}/storage/upload?module=reasignaciones&parent_id=${reasignModal.trabajador_id}`,
                      fd,
                      { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
                    );
                    // 2. Crea la solicitud
                    await axios.post(`${API}/ese/reasignaciones`, {
                      trabajador_id: reasignModal.trabajador_id,
                      motivo: reasignForm.motivo || null,
                      carta_bucket: up.data.bucket,
                      carta_path: up.data.path,
                      carta_filename: reasignForm.file.name,
                    }, { headers });
                    toast.success('Solicitud de reasignación enviada al Capacitador');
                    setReasignModal(null);
                    setForm(emptyForm);
                    setRutError('');
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Error al enviar solicitud');
                  }
                  setReasignBusy(false);
                }}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-semibold disabled:opacity-50"
                data-testid="reasign-submit-btn">
                {reasignBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> Enviando...</> : <><Upload className="w-4 h-4" /> Enviar solicitud</>}
              </button>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
}
